import React, { Fragment, useEffect } from 'react';
import {
  MenuItem,
  FormHelperText,
  Grid,
  Select,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
} from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import { School } from '@mui/icons-material';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import * as Yup from 'yup';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';
import { Link, useLocation } from 'react-router-dom';
import LastUpdated from '../common/LasUpdated';
import InputAdornment from '@mui/material/InputAdornment';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import Checkbox from '@mui/material/Checkbox';

const defaultValues = {
  display_name: '',
  learner_count: '',
  field_worker_id: '',
  school_calendar_id: '',
  head_master: '',
  school_coordinator: '',
  accountingData: {},
  is_active: true,
};

const defaultFilterOptions = {
  schoolNames: [],
  menus: [],
  projects: [],
  fieldWorkers: [],
  schoolCalendars: [],
};

const Manage = () => {
  useEffect(() => {
    document.title = 'PFSA | Schools';
  }, []);
  const location = useLocation();

  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [appliedFilters, setAppliedFilters] = React.useState(['is_active']);
  const [filterOptions, setFilterOptions] = React.useState(defaultFilterOptions);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [updatedDetails, setUpdatedDetails] = React.useState({
    user: '',
    lastUpdated: '',
  });
  const [tableData, setTableData] = React.useState({
    count: 0,
    schools: [],
  });
  const [searchInput, setSearchInput] = React.useState({
    display_name: 'All',
    project_id: 'All',
    standard_menu_id: 'All',
    field_worker_id: 'All',
    is_active: 'All',
    database: 'All',
  });
  const [accountingDbs, setAccountingDbs] = React.useState([]);
  const [currentlyValidating, setCurrentlyValidating] = React.useState([]);
  const [includeDbs, setIncludeDbs] = React.useState([]);
  const [accountingCodeHelp, setAccountingCodeHelp] = React.useState({});
  // Add database validating
  const addValidating = (item) => {
    setCurrentlyValidating((prevItems) => [...prevItems, item]);
  };
  // Remove database validating
  const removeValidating = (rem) => {
    setCurrentlyValidating((prevItems) => prevItems.filter((db) => db !== rem));
  };

  const handleCheckboxChange = (db, event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setIncludeDbs((prevItems) => [...prevItems, db]);
    } else {
      setIncludeDbs((prevItems) => prevItems.filter((entry) => entry !== db));
    }
  };

  const validationSchema = Yup.object().shape({
    display_name: Yup.string().required('Required'),
    head_master: Yup.string().required('Required'),
    school_coordinator: Yup.string().required('Required'),
    is_active: Yup.boolean().required(),
    field_worker_id: Yup.string().required('Required'),
    school_calendar_id: Yup.string().required('Required'),
    learner_count: Yup.string()
      .transform((value) => (parseInt(value) <= 0 ? NaN : value))
      .typeError('Incorrect input')
      .required('Required'),
    accountingData: getAccountingDataValidationSchema(accountingDbs),
  });

  function getAccountingDataValidationSchema(dbs) {
    const schemaFields = {};

    // Loop through each key in accountingCode and create a validation rule
    for (const key of dbs) {
      schemaFields[key] = Yup.object().shape({
        code: Yup.string().nullable(),
        standard_menu_id: Yup.string()
          .nullable()
          .test('Required', 'Required', (val) => {
            if (includeDbs.includes(key)) {
              if (val) return true;
              else return false;
            } else return true;
          }),
        project_id: Yup.string()
          .nullable()
          .test('Required', 'Required', (val) => {
            if (includeDbs.includes(key)) {
              if (val) return true;
              else return false;
            } else return true;
          }),
      });
    }

    return Yup.object().shape(schemaFields);
  }

  async function validateAccountingCode(db, id = undefined) {
    const code = getValues().accountingData?.[db]?.code;
    const schoolId = id ?? modalState.update;

    if (code) {
      try {
        addValidating(db);

        const { data } = await userRequest().get(
          `/accounting-databases/customer-accounting-code?SageDB=${db}&code=${code}`
        );

        removeValidating(db);

        if (
          data.customer.accountingCode &&
          (data.customer?.internalSchool?.id === Number(schoolId) || data.customer?.internalSchool?.id === undefined)
        ) {
          setAccountingCodeHelp((currentState) => {
            return {
              ...currentState,
              [db]: data.customer.accountingDescription,
            };
          });
          return true;
        } else {
          const message = !data.customer.accountingCode
            ? `This code doesn't exist`
            : data.customer.internalSchool
            ? `This code is linked to ${data.customer.internalSchool.display_name}`
            : '';

          setError(`accountingData.${db}.code`, { type: 'custom', message: message });
          return false;
        }
      } catch (error) {
        setError(`accountingData.${db}.code`, { type: 'custom', message: 'Something went wrong when validating' });
        return false;
      }
    } else {
      setAccountingCodeHelp((currentState) => {
        return {
          ...currentState,
          [db]: undefined,
        };
      });
      setError(`accountingData.${db}.code`, { type: 'custom', message: 'Required' });
      return false;
    }
  }

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const schoolId = searchParams.get('schoolId');

    if (schoolId) {
      const school = filterOptions.schoolNames.find((obj) => obj.id === Number(schoolId));

      if (school) {
        setSearchInput((prevState) => ({
          ...prevState,
          display_name: school.display_name,
        }));
      }
    }
  }, [location.search, filterOptions.schoolNames]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    trigger,
    resetField,
    setError,
    getValues,
    formState: { errors, isSubmitted },
    formState,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });
  useFieldArray({
    control,
    name: 'accountingData',
  });

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    getFilterOptions();
  }, []);

  React.useEffect(() => {
    setDataIsFetching((current) => {
      if (!current) return true;
    });
    getSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, page, searchInput]);

  function getDatabases() {
    userRequest()
      .get('/accounting-databases/')
      .then(({ data }) => {
        setAccountingDbs(data.accountingDatabases ?? []);
      });
  }

  function getSchools() {
    setDataIsFetching(true);
    getDatabases();
    userRequest()
      .get(`/schools?per_page=${perPage}&page=${page}&${buildQuery()}`)
      .then(({ data }) => {
        setTableData({ count: data.total, schools: data.schools });
        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.log(response);
        setDataIsFetching(false);
      });
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }

    if (appliedFilters.length > 0) {
      return appliedFilters.every((key) => searchInput[key] === data[key]);
    }
    return true;
  }

  function getFilterOptions() {
    let promises = [
      '/field-workers/names',
      '/menus/names',
      '/projects/names',
      '/schools/names',
      '/school-calendars/names',
    ];

    promises = promises.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error.response);
          return null;
        })
    );

    Promise.all(promises)
      .then((all) => {
        setFilterOptions({
          fieldWorkers: all[0].fieldWorkers,
          menus: all[1].menus,
          projects: all[2].projects,
          schoolNames: all[3].schools,
          schoolCalendars: all[4].schoolCalendars,
        });
      })
      .catch();
  }

  function setStatus(id, data) {
    userRequest()
      .post(`/schools/update/${id}`, data)
      .then(() => {
        let schools = [];
        if (meetFilterConditions(data)) {
          schools = tableData.schools.map((s) => {
            if (id === s.id) s.is_active = !s.is_active;
            return s;
          });
        } else {
          schools = tableData.schools.filter((s) => s.id !== id);
        }
        setTableData(() => {
          return { count: schools.length, schools };
        });
      })
      .catch(() => {});
  }

  const onSubmit = async (data) => {
    toast.info('Validating...', {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'validating',
    });

    const accountingDataToDelete = Object.keys(getValues().accountingData).filter((obj) => !includeDbs.includes(obj));

    accountingDataToDelete.forEach((db) => delete data.accountingData[db]);

    const valid = await Promise.all(includeDbs.map((db) => validateAccountingCode(db)));

    toast.dismiss('validating');

    if (valid.includes(false)) {
      return;
    }

    if (data !== '') {
      toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
        isLoading: true,
        position: 'bottom-center',
        autoClose: 5000,
        theme: 'colored',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'create',
      });
      let api = modalState.update ? `/schools/update/${modalState.update}` : '/schools';

      userRequest()
        .post(api, data)
        .then((res) => {
          toast.dismiss('create');
          toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
          const school = res.data.school;
          setModalState({ update: true, open: false });
          reset(defaultValues, {});
          getSchools();
        })
        .catch(({ response }) => {
          toast.dismiss('create');
          toast.error(response.data.message, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
        });
    }
  };

  function buildQuery(foundFilter = null) {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (foundFilter) foundFilter.push(key);
          let tempKey = key;
          let tempValue = value;
          if (key === 'is_active') {
            if (value === 'ALL') {
              tempKey = '';
              tempValue = '';
            } else if (value === 'ACTIVE') {
              tempValue = 'true';
            } else if (value === 'INACTIVE') {
              tempValue = 'false';
            }
          }
          // Check for "All" and treat it like an empty string
          if (tempValue === 'All') {
            tempKey = '';
            tempValue = '';
          }
          return encodeURIComponent(tempKey) + '=' + encodeURIComponent(tempValue);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');
  }

  function getFilters() {
    const filteredEntries = Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'display_name') {
            if (value === 'All') {
              return [];
            } else {
              return `School Name: ${value}`;
            }
          } else if (key === 'project') {
            if (value === 'All') {
              return [];
            } else {
              return `Project: ${value}`;
            }
          } else if (key === 'menu') {
            if (value === 'All') {
              return [];
            } else {
              return `Menu: ${value}`;
            }
          } else if (key === 'field_worker_id') {
            if (value === 'All') {
              return [];
            } else {
              return `Field Worker: ${filterOptions.fieldWorkers.find((x) => x.id === value)?.name}`;
            }
          } else if (key === 'is_active') {
            if (value === 'ACTIVE') {
              return `Status: Active`;
            } else if (value === 'INACTIVE') {
              return `Status: Inactive`;
            } else if (value === 'All') {
              return [];
            }
          }
        }
        return undefined;
      })
      .filter((x) => x !== undefined && x.length !== 0);

    return filteredEntries;
  }

  const onSearch = (e) => {
    e.preventDefault();

    let foundFilter = [];
    const queryString = buildQuery(foundFilter);

    setAppliedFilters(foundFilter);
    userRequest()
      .get(`/schools?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          schools: data.schools,
        });
      })
      .catch(({ response }) => {
        console.log(response);
      });
  };

  const onChangeInput = (e, val, name = null) => {
    if (name) setSearchInput({ ...searchInput, [name]: val });
    else setSearchInput({ ...searchInput, [e.target.name]: e.target.value });
  };

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      setUpdatedDetails({
        user: `${row.user_name} ${row.user_surname}`,
        lastUpdated: row.last_updated_datetime,
      });
      reset(
        Object.keys(defaultValues).reduce((reduced, curr) => {
          if (curr === 'accountingData') {
            setIncludeDbs(Object.keys(row[curr]));
          }
          reduced[curr] = row[curr];
          return reduced;
        }, {})
      );
      trigger()
        .then(
          Promise.all(
            Object.keys(getValues().accountingData).map(async (db) => await validateAccountingCode(db, row.id))
          )
        )
        .then(
          accountingDbs.forEach((db) => {
            resetField(`accountingData.${db}.standard_menu_id`);
            resetField(`accountingData.${db}.project_id`);
          })
        );
    },
    toggleActive: (row) => {
      setStatus(row.id, { is_active: !row.is_active });
      userRequest()
        .get(`/schools?per_page=${perPage}&page=${page}&${buildQuery()}`)
        .then(({ data }) => {
          setTableData({ count: data.total, schools: data.schools });
          setDataIsFetching(false);
          // fetchUser(data.schools);
        });
    },
  };

  function getAccountingDatagridColumns() {
    return accountingDbs.flatMap((db) => {
      return [
        {
          field: `${db}_accounting_code`,
          headerName: `${db} Code`,
          flex: 1,
          valueGetter: ({ row }) => row.accountingData?.[db]?.code || '',
        },
        {
          field: `${db}_project`,
          headerName: `${db} Project`,
          flex: 1,
          valueGetter: ({ row }) => row.accountingData?.[db]?.project || '',
        },
        {
          field: `${db}_menu`,
          headerName: `${db} Menu`,
          flex: 1,
          valueGetter: ({ row }) => row.accountingData?.[db]?.menu || '',
          renderCell: ({ value }) => (
            <Link to={`/menu-details/manage?display_name=${value}`}>
              <u>{value}</u>
            </Link>
          ),
        },
      ];
    });
  }
  const columns = [
    {
      field: 'display_name',
      headerName: 'School Name',
      flex: 1,
    },
    {
      field: 'learner_count',
      headerName: 'Learners',
      type: 'number',
      flex: 1,
    },
    {
      field: 'field_worker',
      headerName: 'Field Worker',
      flex: 1,
      renderCell: ({ row }) => `${row.fw_name ?? ''} ${row.fw_surname ?? ''}`,
    },
    ...getAccountingDatagridColumns(),
    {
      field: 'is_active',
      headerName: 'Status',
      headerAlign: 'center',
      width: 110,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  return (
    <Fragment>
      <Breadcrumb icon={<School />} title="Manage" parent="Schools" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form" noValidate="" onSubmit={onSearch}>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Database</InputLabel>
                <Select
                  name="database"
                  value={searchInput.database}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Database"
                  style={{
                    fontStyle: searchInput.display_name === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {accountingDbs.map((n, i) => (
                    <MenuItem key={i} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">School Name</InputLabel>
                <Select
                  name="display_name"
                  value={searchInput.display_name}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="School Name"
                  style={{
                    fontStyle: searchInput.display_name === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.schoolNames.map((n, i) => (
                    <MenuItem key={i} value={n.display_name}>
                      {n.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Project</InputLabel>
                <Select
                  name="project_id"
                  value={searchInput.project_id}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Project"
                  style={{
                    fontStyle: searchInput.project === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.projects.map((n, i) => (
                    <MenuItem key={i} value={n.id}>
                      {n.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Menu</InputLabel>
                <Select
                  name="standard_menu_id"
                  value={searchInput.standard_menu_id}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Menu"
                  style={{
                    fontStyle: searchInput.is_active === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.menus.map((n, i) => (
                    <MenuItem key={i} value={n.id}>
                      {n.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Field Worker</InputLabel>
                <Select
                  name="field_worker_id"
                  value={searchInput.field_worker_id}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Field Worker"
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.fieldWorkers.map((n, i) => (
                    <MenuItem key={i} value={n.id}>
                      {n.name} {n.surname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
                <Select
                  name="is_active"
                  value={searchInput.is_active}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Status"
                  style={{
                    fontStyle: searchInput.is_active === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </form>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          setModalState({ update: false, open: true });
          reset();
          setIncludeDbs([]);
          setAccountingCodeHelp({});
          // clearErrors();
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ update: false, open: false });
          reset(defaultValues);
        }}
        size="lg"
        scrollable={true}
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ update: false, open: false });
            reset(defaultValues);
          }}
        >
          {modalState.update ? `Edit School` : 'Create School'}
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                <TextField
                  value={watch('display_name')}
                  label="School Name"
                  variant="outlined"
                  {...register(`display_name`)}
                  error={!!(errors.display_name?.message && isSubmitted && getValues().display_name === '')}
                  // helperText={errors.display_name?.message && isSubmitted ? errors.display_name.message : ''}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>

              <Grid sx={{ paddingBottom: 3 }} gap={3}>
                {accountingDbs.map((db, index) => {
                  return (
                    <Grid key={index}>
                      <FormControlLabel
                        checked={includeDbs.includes(db)}
                        onChange={(event) => handleCheckboxChange(db, event)}
                        control={<Checkbox />}
                        label={`${db} Database`}
                      />
                      {includeDbs.includes(db) && (
                        <Grid sx={{ padding: 3 }} gap={3}>
                          <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                            <Controller
                              render={() => (
                                <TextField
                                  sx={{ flex: 11 }}
                                  autoComplete="off"
                                  label="Accounting Code"
                                  {...register(`accountingData.${db}.code`)}
                                  error={!!errors.accountingData?.[db]?.code}
                                  onBlur={() => validateAccountingCode(db)}
                                  helperText={errors.accountingData?.[db]?.code?.message ?? accountingCodeHelp[db]}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {currentlyValidating.includes(db) && <CloudSyncIcon />}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                              name={`accountingData.${db}.code`}
                              control={control}
                            />
                          </div>
                          <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                            <Controller
                              render={() => (
                                <>
                                  <FormControl
                                    fullWidth
                                    error={
                                      (formState.isSubmitted ||
                                        formState.dirtyFields.accountingData?.[db]?.project_id) &&
                                      !!errors.accountingData?.[db]?.project_id
                                    }
                                  >
                                    <InputLabel id="project-select-label">Project</InputLabel>
                                    <Controller
                                      name={`accountingData.${db}.project_id`}
                                      control={control}
                                      render={({ field }) => (
                                        <Select
                                          labelId="project-select-label"
                                          id="project-select"
                                          {...field}
                                          value={field.value ?? ''}
                                          label="Project"
                                        >
                                          <MenuItem value="">
                                            <em>- Select -</em>
                                          </MenuItem>
                                          {filterOptions.projects.map((p) => (
                                            <MenuItem key={p.id} value={p.id}>
                                              {p.display_name}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      )}
                                    />
                                    <FormHelperText>
                                      {formState.isSubmitted || formState.dirtyFields.accountingData?.[db]?.project_id
                                        ? errors.accountingData?.[db]?.project_id?.message
                                        : null}
                                    </FormHelperText>
                                  </FormControl>
                                </>
                              )}
                              name={`accountingData.${db}.project_id`}
                              control={control}
                            />
                          </div>
                          <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                            <Controller
                              name={`accountingData.${db}.standard_menu_id`}
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  fullWidth
                                  error={
                                    (formState.isSubmitted ||
                                      formState.dirtyFields.accountingData?.[db]?.standard_menu_id) &&
                                    !!errors.accountingData?.[db]?.standard_menu_id
                                  }
                                >
                                  <InputLabel id="menu-select-label">Menu</InputLabel>
                                  <Select
                                    labelId="menu-select-label"
                                    id="menu-select"
                                    {...field} // This includes name, value, onChange, and onBlur
                                    value={field.value ?? ''}
                                    label="Menu"
                                  >
                                    <MenuItem value="">- Select -</MenuItem>
                                    {filterOptions.menus.map((m) => (
                                      <MenuItem key={m.id} value={m.id}>
                                        {m.display_name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  <FormHelperText>
                                    {formState.isSubmitted ||
                                    formState.dirtyFields.accountingData?.[db]?.standard_menu_id
                                      ? errors.accountingData?.[db]?.standard_menu_id?.message
                                      : null}
                                  </FormHelperText>
                                </FormControl>
                              )}
                            />
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
              <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                <TextField
                  id="outlined-number"
                  label="Learner Count"
                  type="number"
                  {...register(`learner_count`)}
                  error={!!(errors.learner_count?.message && isSubmitted && getValues().learner_count === '')}
                  // helperText={errors.learner_count?.message && isSubmitted ? errors.learner_count.message : ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                <Controller
                  control={control}
                  name="school_calendar_id"
                  defaultValue={''}
                  render={({ field }) => (
                    <FormControl
                      error={
                        !!(errors.school_calendar_id?.message && isSubmitted && getValues().school_calendar_id === '')
                      }
                    >
                      <InputLabel id="demo-simple-select-helper-label">School Calendar</InputLabel>
                      <Select
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="School Calendar"
                      >
                        <MenuItem value={''}>- Select -</MenuItem>
                        {filterOptions.schoolCalendars.map((sc) => (
                          <MenuItem key={sc.id} value={sc.id}>
                            {sc.display_name}
                          </MenuItem>
                        ))}
                      </Select>
                      {/* <FormHelperText>
                      {errors.school_calendar_id?.message && isSubmitted ? errors.school_calendar_id.message : ''}
                      </FormHelperText>  */}
                    </FormControl>
                  )}
                />
              </div>

              <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                <Controller
                  control={control}
                  name="field_worker_id"
                  defaultValue={''}
                  render={({ field }) => (
                    <FormControl
                      error={!!(errors.field_worker_id?.message && isSubmitted && getValues().field_worker_id === '')}
                    >
                      <InputLabel id="demo-simple-select-helper-label">Field Worker</InputLabel>
                      <Select
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        labelId="demo-simple-select-helper-label"
                        id="demo-simple-select-helper"
                        label="Field Worker"
                      >
                        <MenuItem value={''}>- Select -</MenuItem>
                        {filterOptions.fieldWorkers.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.name} {m.surname}
                          </MenuItem>
                        ))}
                      </Select>
                      {/* <FormHelperText>
                      {errors.field_worker_id?.message && isSubmitted ? errors.field_worker_id.message : ''}
                      </FormHelperText> */}
                    </FormControl>
                  )}
                />
              </div>
              <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                <TextField
                  value={watch('head_master')}
                  label="Head Master"
                  variant="outlined"
                  {...register(`head_master`)}
                  // helperText={errors.head_master?.message && isSubmitted ? errors.head_master.message : ''}
                  error={!!(errors.head_master?.message && isSubmitted && getValues().head_master === '')}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="form-group d-flex gap-4 flex-wrap row mx-0">
                <TextField
                  value={watch('school_coordinator')}
                  label="School Coordinator"
                  variant="outlined"
                  {...register(`school_coordinator`)}
                  // helperText={errors.school_coordinator?.message && isSubmitted ? errors.school_coordinator.message : ''}
                  error={!!(errors.school_coordinator?.message && isSubmitted && getValues().school_coordinator === '')}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>

              {modalState.update && (
                // school manage controller does not have last updated username
                <LastUpdated user={updatedDetails.user} datetime={updatedDetails.lastUpdated} />
              )}
              {modalState.update && (
                <div className="d-flex justify-content-start my-4">
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register(`is_active`)} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                </div>
              )}
              <div>
                <button className="btn btn-primary me-1" type="submit">
                  {modalState.update ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.schools}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

export default Manage;
